import React, { useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import useSWR from 'swr';
import { motion, AnimatePresence } from 'framer-motion';
import pb from './lib/pocketbase';
import { useReferencesData } from './context/ReferencesContext';
import * as FiIcons from 'react-icons/fi';
import './RecruitmentComponents.css'; // For branded loader styles

const fetcher = async (key) => {
  return await pb.collection('references').getFirstListItem(key);
};

const similarFetcher = async (key) => {
  const { filter, sort } = JSON.parse(key);
  const res = await pb.collection('references').getList(1, 12, { filter, sort });
  return res.items;
};

export default function ReferenceDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { references, loading: globalLoading } = useReferencesData();

  // Scroll reset handled by motion exit/enter usually, but we keep a backup
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [slug]);

  const sliderRef = useRef(null);
  const [activeImg, setActiveImg] = useState(0);

  const [activeSize, setActiveSize] = useState({ w: 4, h: 3 });
  const [fading, setFading] = useState(false);

  const filter = useMemo(() => `(slug = "${slug}" || id = "${slug}") && published = true`, [slug]);

  // Main Item logic: try context first, then fallback to SWR if not found (direct link case)
  const contextItem = useMemo(() => {
    return references.find(r => r.slug === slug || r.id === slug);
  }, [references, slug]);

  const { data: swrItem, error } = useSWR(
    !contextItem ? filter : null,
    fetcher,
    { shouldRetryOnError: false, revalidateOnFocus: false }
  );

  const item = contextItem || swrItem;
  const loading = globalLoading && !item;
  const notFound = !!error && !contextItem;

  const logoUrl = (r) => (r?.logo ? pb.files.getURL(r, r.logo) : null);
  const heroImageUrl = (r) => (r?.logo ? pb.files.getURL(r, r.logo) : undefined);
  const galleryUrls = (r) => (r?.images || []).map((fn) => pb.files.getURL(r, fn));

  const modules = Array.isArray(item?.modules) ? item.modules : [];
  const interventions = Array.isArray(item?.interventions) ? item.interventions : [];



  // Similar items logic: use context data for instant similar projects
  const gallery = galleryUrls(item);
  const similar = useMemo(() => {
    if (!item?.id) return [];
    return references
      .filter(r => r.id !== item.id && r.published)
      .slice(0, 12); // Limit to 12 as per original SWR query
  }, [references, item?.id]);

  // Update aspect ratio when active image changes
  useEffect(() => {
    if (!gallery.length) return;
    const src = gallery[activeImg];
    if (!src) return;
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth || 4;
      const h = img.naturalHeight || 3;
      setActiveSize({ w, h });
    };
    img.src = src;
  }, [activeImg, gallery]);

  const shouldShowClient = item?.show_client !== false;

  return (
    <React.Fragment key={slug}>
      <motion.section
        className="ref-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="section-inner ref-hero-inner">
          <motion.div
            className="ref-hero-avatar"
            layoutId={`hero-avatar-${slug}`} // Connects to the clicked similar card
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            {shouldShowClient && logoUrl(item) ? (
              <img className="ref-hero-logo" src={heroImageUrl(item)} alt="Logo" />
            ) : (
              <span>{(shouldShowClient ? (item?.client?.[0] || 'R') : (item?.titre?.[0] || 'R')).toUpperCase()}</span>
            )}
          </motion.div>
          <motion.h1
            className="ref-hero-title"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {item ? (item.titre || item.client || 'Référence') : 'Référence'}
          </motion.h1>
        </div>
      </motion.section>
      <section className="section">
        <div className="section-inner">
          <nav className="breadcrumb">
            <Link to="/">Accueil</Link>
            <span>/</span>
            <Link to="/references">Références</Link>
            <span>/</span>
            <span>{shouldShowClient ? (item?.client || 'Client') : 'Détail'}</span>
          </nav>
          {/* Space removed here for cleaner flow */}

          {loading ? (
            <div className="branded-loader-container" style={{ minHeight: '400px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <div className="loader-visual" style={{ transform: 'scale(0.8)', marginBottom: '1rem' }}>
                <div className="loader-orbit orbit-1"></div>
                <div className="loader-orbit orbit-2"></div>
                <div className="loader-orbit orbit-3"></div>
                <div className="loader-logo-wrapper">
                  <img src="/images/logo.png" alt="Version Net" className="loader-logo" />
                </div>
              </div>
              <p style={{ color: 'var(--text-muted)', fontWeight: 500 }}>Préparation de la présentation...</p>
            </div>
          ) : notFound ? (
            <div className="card" style={{ padding: '1rem' }}>
              <h3 style={{ marginTop: 0 }}>Référence introuvable</h3>
              <p>La page demandée n'existe pas ou n'est plus publiée.</p>
              <Link to="/references" className="btn">Retour aux références</Link>
            </div>
          ) : item ? (
            <div className="ref-detail-grid">

              <div className="ref-main-column">
                <section className="card ref-unified-card">
                  {/* Part 1: Identity/Brand */}
                  <div className="ref-intro-section" style={{ display: 'flex', gap: '2.5rem', alignItems: 'flex-start', borderBottom: '1px solid var(--border-subtle)', paddingBottom: '2.5rem', marginBottom: '2.5rem' }}>
                    {/* Part 1: Identity/Brand (Logo Left) */}
                    <div className="ref-brand" style={{ flex: '0 0 160px' }}>
                      {shouldShowClient && logoUrl(item) ? (
                        <img src={logoUrl(item)} alt="Logo client" />
                      ) : (
                        <div className="ref-brand-placeholder">{(shouldShowClient ? (item.client?.[0] || 'R') : (item.titre?.[0] || 'R')).toUpperCase()}</div>
                      )}
                    </div>

                    {/* Part 3: Resume (Text Right) */}
                    {item.resume ? (
                      <div className="ref-resume-col" style={{ flex: 1 }}>
                         <h3 style={{ fontSize: '1.2rem', color: 'var(--primary)', marginBottom: '1rem', marginTop: 0 }}>Description & Enjeux</h3>
                         <p style={{ fontSize: '1.1rem', lineHeight: '1.7', color: 'var(--text-main)', whiteSpace: 'pre-line', margin: 0 }}>
                           {item.resume}
                         </p>
                      </div>
                    ) : null}
                  </div>

                  {/* Part 4: Modules Clés */}
                  {((modules && modules.length > 0) || item.modules_intro) ? (
                    <div className="ref-section" style={{ marginBottom: '2rem' }}>
                      <h3 style={{ fontSize: '1.2rem', color: 'var(--primary)', marginBottom: '1rem' }}>Modules clés</h3>
                      {item.modules_intro && (
                        <p style={{ marginBottom: '1rem', opacity: 0.9, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{item.modules_intro}</p>
                      )}
                      {modules && modules.length > 0 && (
                        <ul className="ref-modules-list" style={{ paddingLeft: '1.2rem', margin: 0 }}>
                          {modules.map((m, i) => (
                            <li key={i} style={{ marginBottom: '0.5rem' }}>{m}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : null}

                  {/* Part 2: Interventions */}
                  {interventions.length ? (
                    <div className="ref-section" style={{ marginBottom: '2rem' }}>
                      <h3 style={{ fontSize: '1.2rem', color: 'var(--primary)', marginBottom: '1rem' }}>Interventions</h3>
                      <ul className="ref-interventions-list" style={{ padding: 0, margin: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem', listStyle: 'none' }}>
                        {interventions.map((it, i) => {
                          const item = typeof it === 'string' ? { text: it, icon: 'FiZap' } : it;
                          const IconComp = FiIcons[item.icon] || FiIcons.FiZap;
                          return (
                            <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontWeight: 500 }}>
                              <div style={{ color: 'var(--primary)', display: 'flex' }}>
                                <IconComp size={18} />
                              </div>
                              <span style={{ fontSize: '1.05rem' }}>{item.text}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ) : null}
                </section>

                <div className="ref-actions" style={{ marginTop: '1rem' }}>
                  <Link to="/references" className="btn btn-ghost">← Retour aux références</Link>
                </div>
              </div>

              <aside className="card ref-aside">
                {gallery.length ? (
                  <section className="ref-section" style={{ margin: 0 }}>
                    <h4 style={{ marginBottom: '1rem' }}>Galerie du projet</h4>
                    <div className="gallery-product">
                      <div className="gallery-thumbs" role="list">
                        {gallery.map((u, idx) => (
                          <button
                            key={u}
                            type="button"
                            className={`thumb ${idx === activeImg ? 'thumb-active' : ''}`}
                            onClick={() => {
                              if (idx !== activeImg) {
                                setFading(true);
                                setActiveImg(idx);
                                setTimeout(() => setFading(false), 180);
                              }
                            }}
                            aria-label={`Image ${idx + 1}`}
                          >
                            <img src={u} alt="Miniature" />
                          </button>
                        ))}
                      </div>
                      <div
                        className={`gallery-main ${fading ? 'fading' : ''}`}
                        style={{
                          backgroundImage: `url(${gallery[activeImg]})`,
                          aspectRatio: `${activeSize.w} / ${activeSize.h}`,
                          cursor: 'default'
                        }}
                        aria-label="Image principale"
                      />
                    </div>
                  </section>
                ) : (
                  <p className="text-muted" style={{ textAlign: 'center', fontSize: '0.9rem' }}>Pas d'images pour ce projet.</p>
                )}
              </aside>
            </div>
          ) : null}


          {item && similar.length ? (
            <div className="section" style={{ paddingTop: '1.5rem' }}>
              <div className="section-inner">
                <div className="section-header" style={{ marginBottom: '1rem' }}>
                  <h2 style={{ marginBottom: '.25rem' }}>Projets similaires</h2>
                  <p>Découvrez d'autres projets susceptibles de vous intéresser.</p>
                </div>
                <div className="similar-wrapper">
                  <button type="button" className="similar-arrow similar-arrow-left" onClick={() => sliderRef.current?.scrollBy({ left: -280, behavior: 'smooth' })} aria-label="Précédent">‹</button>
                  <div className="similar-slider" ref={sliderRef}>
                    {similar.map((r) => (
                      <motion.div
                        key={r.id}
                        className="similar-card"
                        role="link"
                        tabIndex={0}
                        layoutId={`hero-avatar-${r.slug || r.id}`} // The source of the morph
                        onClick={() => {
                          navigate(`/references/${r.slug || r.id}`);
                        }}
                        whileHover={{ 
                          y: -8, 
                          boxShadow: "0 12px 30px rgba(0,0,0,0.15)",
                          transition: { duration: 0.2 } 
                        }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {r.logo ? (
                          <img src={pb.files.getURL(r, r.logo)} alt={r.titre || 'Référence'} />
                        ) : (
                          <div className="similar-card-placeholder">{(r.client?.[0] || 'R').toUpperCase()}</div>
                        )}
                        <div className="similar-card-title">{r.titre || r.client}</div>
                      </motion.div>
                    ))}
                  </div>
                  <button type="button" className="similar-arrow similar-arrow-right" onClick={() => sliderRef.current?.scrollBy({ left: 280, behavior: 'smooth' })} aria-label="Suivant">›</button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </section>
    </React.Fragment>
  );
}
